import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { ADMIN_BASE_PATH } from "@/lib/constants/adminRoutes";
import RequireAuth from "@/components/RequireAuth";
import RequireOnboarding from "@/components/RequireOnboarding";
import RequireApproval from "@/components/RequireApproval";
import RequireAdmin from "@/components/RequireAdmin";
import AppLayout from "@/components/layout/AppLayout";
import { initGA, logPageView } from "@/lib/analytics";
import CookieConsent from "@/components/CookieConsent";
import { ThemeProvider } from "@/context/ThemeContext";
import { LessonDataProvider } from "@/context/LessonDataContext";
import { ZillAiProvider } from "@/context/ZillAiContext";
import { LessonGenerationProvider } from "@/context/LessonGenerationContext";
import ZillAiWidget from "@/components/ZillAiWidget";
import { LessonReadyToast } from "@/components/LessonReadyToast";

// Eagerly loaded — public pages a visitor may land on immediately
import Home from "@/routes/pages/Home";
import HowItWorks from "@/routes/pages/HowItWorks";
import ComparePlans from "@/routes/pages/ComparePlans";
import Login from "@/routes/pages/login";

// Lazy-loaded — not needed until user navigates past the home page
const PasswordReset = lazy(() => import("@/routes/pages/PasswordReset"));
const PrivacyPolicy = lazy(() => import("@/routes/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/routes/pages/TermsOfService"));
const ApprovalSuccess = lazy(() => import("@/pages/ApprovalSuccess"));
const ApprovalError = lazy(() => import("@/pages/ApprovalError"));

// Onboarding
const OnboardingWizard = lazy(() => import("@/features/onboarding/routes/OnboardingWizard"));
const OnboardingSuccess = lazy(() => import("@/features/onboarding/routes/OnboardingSuccess"));
const OnboardingProPlusSuccess = lazy(() => import("@/features/onboarding/routes/OnboardingProPlusSuccess"));
const OnboardingFamilySuccess = lazy(() => import("@/features/onboarding/routes/OnboardingFamilySuccess"));
const OnboardingFreeSuccess = lazy(() => import("@/features/onboarding/routes/OnboardingFreeSuccess"));
const OnboardingChildSuccess = lazy(() => import("@/features/onboarding/routes/OnboardingChildSuccess"));
const OnboardingCanceled = lazy(() => import("@/features/onboarding/routes/OnboardingCanceled"));
const OnboardingOtp = lazy(() => import("@/features/onboarding/routes/otp"));

// Authenticated app pages
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const LearningCenter = lazy(() => import("@/pages/LearningCenter"));
const DomainDetail = lazy(() => import("@/pages/DomainDetail"));
const LessonPage = lazy(() => import("@/pages/LessonPage"));
const LessonCatalogPage = lazy(() => import("@/pages/LessonCatalogPage"));
const LessonLandingPage = lazy(() => import("@/pages/LessonLandingPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const ProfileSummaryPage = lazy(() => import("@/pages/ProfileSummaryPage"));
const ProgressDetailPage = lazy(() => import("@/pages/ProgressDetailPage"));
const Journal = lazy(() => import("@/routes/JournalPage"));
const JournalNewEntry = lazy(() => import("@/routes/NewJournalEntry"));
const ResilienceStatusPage = lazy(() => import("@/routes/ResilienceStatusPage"));
const ActivityPage = lazy(() => import("@/routes/ActivityPage"));
const FriendsPage = lazy(() => import("@/routes/FriendsPage"));
const AccomplishmentsPage = lazy(() => import("@/pages/AccomplishmentsPage").then(m => ({ default: m.AccomplishmentsPage })));
const FriendsAccomplishmentsPage = lazy(() => import("@/pages/FriendsAccomplishmentsPage").then(m => ({ default: m.FriendsAccomplishmentsPage })));
const TracksPage = lazy(() => import("@/pages/TracksPage"));
const DailyChallengePage = lazy(() => import("@/pages/DailyChallengePage"));
const DailyChallengePlayPage = lazy(() => import("@/pages/DailyChallengePlayPage"));
const DailyCrosswordPage = lazy(() => import("@/pages/DailyCrosswordPage"));
const DailyCrosswordPlayPage = lazy(() => import("@/pages/DailyCrosswordPlayPage"));
const DailyWordSearchPlayPage = lazy(() => import("@/pages/DailyWordSearchPlayPage"));

// Family
const FamilyDashboard = lazy(() => import("@/pages/FamilyDashboard"));
const FamilyInviteConfirmPage = lazy(() => import("@/pages/FamilyInviteConfirmPage"));
const ChildDashboard = lazy(() => import("@/pages/parent-portal/ChildDashboard"));
const AssignLessonPage = lazy(() => import("@/pages/AssignLessonPage"));
const ParentCuratedPage = lazy(() => import("@/pages/ParentCuratedPage"));
const ParentLessonStudio = lazy(() => import("@/pages/ParentLessonStudio"));
const ParentLessonCreator = lazy(() => import("@/pages/ParentLessonCreator"));
const ParentLessonAssign = lazy(() => import("@/pages/ParentLessonAssign"));
const ParentLessonLanding = lazy(() => import("@/pages/ParentLessonLanding"));
const ParentLessonPlayer = lazy(() => import("@/pages/ParentLessonPlayer"));

// Admin
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminTrackCurator = lazy(() => import("@/pages/admin/AdminTrackCurator"));
const AdminFeedbackViewer = lazy(() => import("@/pages/admin/AdminFeedbackViewer"));
const AdminContactMessages = lazy(() => import("@/pages/admin/AdminContactMessages"));
const AdminPopularCourses = lazy(() => import("@/pages/admin/AdminPopularCourses"));
const AdminCurriculumEditor = lazy(() => import("@/pages/admin/AdminCurriculumEditor"));
const AdminLessonCreator = lazy(() => import("@/pages/admin/AdminLessonCreator"));
const AdminDomainManager = lazy(() => import("@/pages/admin/AdminDomainManager"));
const AdminLessonExport = lazy(() => import("@/pages/admin/AdminLessonExport"));
const AdminZillAiMonitoring = lazy(() => import("@/pages/admin/AdminZillAiMonitoring"));
const AdminGrantInteractions = lazy(() => import("@/pages/admin/AdminGrantInteractions"));
const AdminSecuritySettings = lazy(() => import("@/pages/admin/AdminSecuritySettings"));

export default function App() {
  const location = useLocation();

  // Initialize Google Analytics on mount
  useEffect(() => {
    initGA();
  }, []);

  // Track page views on route changes
  useEffect(() => {
    logPageView(location.pathname + location.search, document.title);
  }, [location]);

  return (
    <>
      <CookieConsent />
      <Suspense fallback={null}>
      <Routes>
      {/* Public routes - no beta wall */}
      <Route path="/" element={<Home />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/compare-plans" element={<ComparePlans />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/reset-password" element={<PasswordReset />} />
      <Route path="/approval-success" element={<ApprovalSuccess />} />
      <Route path="/approval-error" element={<ApprovalError />} />
      
      <Route path="/login" element={<Login />} />

      {/* Lesson landing page - requires authentication */}
      <Route 
        path="/lesson/:domainId/:lessonId" 
        element={
          <RequireAuth>
            <RequireApproval>
              <ThemeProvider>
                <LessonLandingPage />
              </ThemeProvider>
            </RequireApproval>
          </RequireAuth>
        } 
      />

      {/* Onboarding routes (outside app layout) */}
      <Route
        path="/onboarding"
        element={
          <RequireAuth>
            <ThemeProvider>
              <OnboardingWizard />
            </ThemeProvider>
          </RequireAuth>
        }
      />
      <Route path="/onboarding/success" element={<OnboardingSuccess />} />
      <Route path="/onboarding/proplus-success" element={<OnboardingProPlusSuccess />} />
      <Route path="/onboarding/family-success" element={<OnboardingFamilySuccess />} />
      <Route path="/onboarding/free-success" element={<OnboardingFreeSuccess />} />
      <Route path="/onboarding/child-success" element={<OnboardingChildSuccess />} />
      <Route path="/onboarding/canceled" element={<OnboardingCanceled />} />
      <Route path="/onboarding/otp" element={<OnboardingOtp />} />

      {/* App layout wrapper - all authenticated app pages render inside the drawer */}
      <Route
        element={
          <RequireAuth>
            <RequireApproval>
              <RequireOnboarding>
                <ThemeProvider>
                  <LessonDataProvider>
                    <ZillAiProvider>
                      <LessonGenerationProvider>
                        <AppLayout />
                        <ZillAiWidget />
                        <LessonReadyToast />
                      </LessonGenerationProvider>
                    </ZillAiProvider>
                  </LessonDataProvider>
                </ThemeProvider>
              </RequireOnboarding>
            </RequireApproval>
          </RequireAuth>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/progress" element={<ProgressDetailPage />} />
        <Route path="/profile-summary" element={<ProfileSummaryPage />} />
        <Route path="/journal" element={<Journal />} />
        <Route path="/journal/new" element={<JournalNewEntry />} />
        <Route path="/resilience-status" element={<ResilienceStatusPage />} />
        <Route path="/activity" element={<ActivityPage />} />
        <Route path="/friends" element={<FriendsPage />} />
        <Route path="/accomplishments" element={<AccomplishmentsPage />} />
        <Route path="/accomplishments/friends" element={<FriendsAccomplishmentsPage />} />
        <Route path="/family-dashboard" element={<FamilyDashboard />} />
        <Route path="/family-dashboard/child/:childId" element={<ChildDashboard />} />
        <Route path="/family-dashboard/assign/:childId" element={<AssignLessonPage />} />
        <Route path="/family/parent-lesson-studio" element={<ParentLessonStudio />} />
        <Route path="/family/parent-lesson-studio/create" element={<ParentLessonCreator />} />
        <Route path="/family/parent-lesson-studio/:lessonId/assign" element={<ParentLessonAssign />} />
        <Route path="/family-invite" element={<FamilyInviteConfirmPage />} />

        <Route path="/learning" element={<LearningCenter />} />
        <Route path="/learning/catalog" element={<LessonCatalogPage />} />
        <Route path="/learning/:domainId" element={<DomainDetail />} />
        <Route path="/learning/lesson/:domainId/:lessonId" element={<LessonPage />} />
        <Route path="/learning/lesson/parent-created/:lessonId" element={<ParentLessonLanding />} />
        <Route path="/learning/lesson/parent-created/:lessonId/play" element={<ParentLessonPlayer />} />
        <Route path="/parent-curated" element={<ParentCuratedPage />} />
        <Route path="/tracks" element={<TracksPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/daily-challenge" element={<DailyChallengePage />} />
        <Route path="/daily-challenge/play" element={<DailyChallengePlayPage />} />
        <Route path="/daily-crossword" element={<DailyCrosswordPage />} />
        <Route path="/daily-crossword/play" element={<DailyCrosswordPlayPage />} />
        <Route path="/daily-word-search/play" element={<DailyWordSearchPlayPage />} />
        {/* Admin routes */}
        <Route path={ADMIN_BASE_PATH} element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
        <Route path={`${ADMIN_BASE_PATH}/tracks`} element={<RequireAdmin><AdminTrackCurator /></RequireAdmin>} />
        <Route path={`${ADMIN_BASE_PATH}/feedback`} element={<RequireAdmin><AdminFeedbackViewer /></RequireAdmin>} />
        <Route path={`${ADMIN_BASE_PATH}/contact-messages`} element={<RequireAdmin><AdminContactMessages /></RequireAdmin>} />
        <Route path={`${ADMIN_BASE_PATH}/zillai-monitoring`} element={<RequireAdmin><AdminZillAiMonitoring /></RequireAdmin>} />
        <Route path={`${ADMIN_BASE_PATH}/grant-interactions`} element={<RequireAdmin><AdminGrantInteractions /></RequireAdmin>} />
        <Route path={`${ADMIN_BASE_PATH}/popular-courses`} element={<RequireAdmin><AdminPopularCourses /></RequireAdmin>} />
        <Route path={`${ADMIN_BASE_PATH}/curriculum`} element={<RequireAdmin><AdminCurriculumEditor /></RequireAdmin>} />
        <Route path={`${ADMIN_BASE_PATH}/curriculum/new`} element={<RequireAdmin><AdminLessonCreator /></RequireAdmin>} />
        <Route path={`${ADMIN_BASE_PATH}/domains`} element={<RequireAdmin><AdminDomainManager /></RequireAdmin>} />
        <Route path={`${ADMIN_BASE_PATH}/export`} element={<RequireAdmin><AdminLessonExport /></RequireAdmin>} />
        <Route path={`${ADMIN_BASE_PATH}/security`} element={<RequireAdmin><AdminSecuritySettings /></RequireAdmin>} />
      </Route>
    </Routes>
    </Suspense>
    </>
  );
}
